import { useEffect, useState } from 'react';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { adminList, adminUpsertSingleton } from '../../../lib/adminApi';
import { SectionHeader, SaveBar, useSaver, Field, Card } from './_ui';
import ImagePicker from './ImagePicker';

const EMPTY = {
  bannerEnabled: true,
  bannerText: '',
  bannerSubtext: '',
  ctaPrimaryLabel: '',
  ctaPrimaryUrl: '',
  ctaSecondaryLabel: '',
  ctaSecondaryUrl: '',
  responseTime: '',
  howItWorksImage: '',
  aboutImage: '',
};

export default function SettingsSection() {
  const [state, setState] = useState(EMPTY);
  const [loaded, setLoaded] = useState(false);
  const saver = useSaver();

  useEffect(() => {
    (async () => {
      const doc = await adminList('site-settings');
      setState({ ...EMPTY, ...(doc || {}) });
      setLoaded(true);
    })();
  }, []);

  if (!loaded) return <p className="text-brand-ink/60">Loading...</p>;

  const save = () => saver.run(() => adminUpsertSingleton('site-settings', state));

  return (
    <div className="max-w-3xl">
      <SectionHeader
        title="Site Settings"
        description="Global text that appears in multiple places on the site."
      />

      <Card className="mb-6">
        <div className="text-sm font-semibold text-brand-ink mb-3">Sticky enrolment banner</div>
        <label className="inline-flex items-center gap-2 text-sm mb-3">
          <Switch
            checked={state.bannerEnabled}
            onCheckedChange={(v) => setState({ ...state, bannerEnabled: v })}
          />
          Show banner
        </label>
        <Field label="Banner headline">
          <Input value={state.bannerText} onChange={(e) => setState({ ...state, bannerText: e.target.value })} />
        </Field>
        <div className="mt-3">
          <Field label="Banner subtext">
            <Input value={state.bannerSubtext} onChange={(e) => setState({ ...state, bannerSubtext: e.target.value })} />
          </Field>
        </div>
      </Card>

      <Card className="mb-6">
        <div className="text-sm font-semibold text-brand-ink mb-3">Global CTA buttons</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Primary CTA label">
            <Input value={state.ctaPrimaryLabel} onChange={(e) => setState({ ...state, ctaPrimaryLabel: e.target.value })} />
          </Field>
          <Field label="Primary CTA URL">
            <Input value={state.ctaPrimaryUrl} onChange={(e) => setState({ ...state, ctaPrimaryUrl: e.target.value })} />
          </Field>
          <Field label="Secondary CTA label">
            <Input value={state.ctaSecondaryLabel} onChange={(e) => setState({ ...state, ctaSecondaryLabel: e.target.value })} />
          </Field>
          <Field label="Secondary CTA URL">
            <Input value={state.ctaSecondaryUrl} onChange={(e) => setState({ ...state, ctaSecondaryUrl: e.target.value })} />
          </Field>
        </div>
      </Card>

      <Card>
        <Field label="Response-time wording" hint='shown on the Contact page'>
          <Textarea
            rows={2}
            value={state.responseTime}
            onChange={(e) => setState({ ...state, responseTime: e.target.value })}
          />
        </Field>
      </Card>

      <Card className="mt-6">
        <div className="text-sm font-semibold text-brand-ink mb-3">Section photos</div>
        <div className="grid md:grid-cols-2 gap-6">
          <ImagePicker
            label={'"How it works" section image'}
            value={state.howItWorksImage}
            onChange={(url) => setState({ ...state, howItWorksImage: url })}
            size="lg"
          />
          <ImagePicker
            label={'"About" section image'}
            value={state.aboutImage}
            onChange={(url) => setState({ ...state, aboutImage: url })}
            size="lg"
          />
        </div>
      </Card>

      <div className="mt-5">
        <SaveBar {...saver} onSave={save} dataTestId="save-settings" />
      </div>
    </div>
  );
}
